import {
  IVisibilityRule,
  IVisibilityCondition,
  ILabel,
} from '../constants/interfaces';
import { IFormStateValue } from './formUtils';

/**
 * Evaluates a single visibility condition against the form state
 * @param condition - The condition to evaluate
 * @param formState - Current form state
 * @returns boolean indicating if the condition is met
 */
function evaluateCondition(
  condition: IVisibilityCondition,
  formState: Record<string, IFormStateValue>
): boolean {
  const fieldValue = formState[condition.label_id]?.value;
  const { value: expectedValue, operator = 'equals' } = condition;

  if (fieldValue === null || fieldValue === undefined) {
    return false;
  }

  const fieldValueStr = String(fieldValue).toLowerCase().trim();

  switch (operator) {
    case 'equals':
      return fieldValueStr === String(expectedValue).toLowerCase().trim();

    case 'not_equals':
      return fieldValueStr !== String(expectedValue).toLowerCase().trim();

    case 'contains':
      return fieldValueStr.includes(String(expectedValue).toLowerCase().trim());

    case 'in':
      const values = Array.isArray(expectedValue)
        ? expectedValue
        : [expectedValue];
      return values.some(
        (val) => fieldValueStr === String(val).toLowerCase().trim()
      );

    case 'not_in':
      const notValues = Array.isArray(expectedValue)
        ? expectedValue
        : [expectedValue];
      return !notValues.some(
        (val) => fieldValueStr === String(val).toLowerCase().trim()
      );

    case 'greater_than':
      const fieldValueNum = Number(fieldValue);
      const expectedValueNum = Number(expectedValue);
      if (isNaN(fieldValueNum) || isNaN(expectedValueNum)) {
        return false;
      }
      return fieldValueNum > expectedValueNum;

    case 'less_than':
      const fieldValueNumLT = Number(fieldValue);
      const expectedValueNumLT = Number(expectedValue);
      if (isNaN(fieldValueNumLT) || isNaN(expectedValueNumLT)) {
        return false;
      }
      return fieldValueNumLT < expectedValueNumLT;

    case 'greater_than_or_equal':
      const fieldValueNumGTE = Number(fieldValue);
      const expectedValueNumGTE = Number(expectedValue);
      if (isNaN(fieldValueNumGTE) || isNaN(expectedValueNumGTE)) {
        return false;
      }
      return fieldValueNumGTE >= expectedValueNumGTE;

    case 'less_than_or_equal':
      const fieldValueNumLTE = Number(fieldValue);
      const expectedValueNumLTE = Number(expectedValue);
      if (isNaN(fieldValueNumLTE) || isNaN(expectedValueNumLTE)) {
        return false;
      }
      return fieldValueNumLTE <= expectedValueNumLTE;

    default:
      return false;
  }
}

/**
 * Evaluates a complete visibility rule with multiple conditions
 * @param rule - The visibility rule to evaluate
 * @param formState - Current form state
 * @returns boolean indicating if the rule is satisfied
 */
function evaluateVisibilityRule(
  rule: IVisibilityRule,
  formState: Record<string, IFormStateValue>
): boolean {
  const { operator = 'AND', conditions } = rule;

  if (conditions.length === 0) return true;

  if (operator === 'AND') {
    return conditions.every((condition) => {
      // Handle nested rules (for complex OR/AND combinations)
      if (
        'conditions' in condition &&
        Array.isArray((condition as IVisibilityRule).conditions)
      ) {
        return evaluateVisibilityRule(condition as IVisibilityRule, formState);
      }
      return evaluateCondition(condition, formState);
    });
  } else {
    return conditions.some((condition) => {
      // Handle nested rules (for complex OR/AND combinations)
      if (
        'conditions' in condition &&
        Array.isArray((condition as IVisibilityRule).conditions)
      ) {
        return evaluateVisibilityRule(condition as IVisibilityRule, formState);
      }
      return evaluateCondition(condition, formState);
    });
  }
}

/**
 * Determines if a field should be visible based on its visibility rules
 * Supports both new JSON-based rules and handles fields without rules
 * @param field - The field to check visibility for
 * @param formState - Current form state
 * @returns boolean indicating if the field should be visible
 */
export function isFieldVisible(
  field: ILabel,
  formState: Record<string, IFormStateValue>
): boolean {
  // Handle JSON-based visibility rules
  if (field.visibilityRules) {
    let parsedRules: IVisibilityRule;

    // Parse JSON string if needed
    if (typeof field.visibilityRules === 'string') {
      try {
        parsedRules = JSON.parse(field.visibilityRules);
      } catch (error) {
        console.warn(
          `Failed to parse visibility rules for field ${field.labelId}:`,
          error
        );
        return false;
      }
    } else {
      parsedRules = field.visibilityRules;
    }

    // Check if rules have conditions
    if (
      parsedRules &&
      parsedRules.conditions &&
      parsedRules.conditions.length > 0
    ) {
      return evaluateVisibilityRule(parsedRules, formState);
    }
  }

  // If no visibility rules are defined, field is always visible
  // (This includes table children which are handled by their parent's visibility)
  return true;
}

/**
 * Helper function to create visibility rules programmatically
 * @param operator - AND or OR operator for combining conditions
 * @param conditions - Array of conditions
 * @param parentLabelId - Optional parent label ID for structural relationships
 * @returns IVisibilityRule object
 */
export function createVisibilityRule(
  operator: 'AND' | 'OR' = 'AND',
  conditions: IVisibilityCondition[] = [],
  parentLabelId?: number
): IVisibilityRule {
  const rule: IVisibilityRule = {
    conditions,
    operator,
  };

  if (parentLabelId !== undefined) {
    rule.parent_label_id = parentLabelId;
  }

  return rule;
}

/**
 * Extracts the parent_label_id from a field's visibility rules
 * @param field - The field to check
 * @returns The parent label ID if found, undefined otherwise
 */
export function getParentLabelId(field: ILabel): number | undefined {
  if (!field.visibilityRules) {
    return undefined;
  }

  let parsedRules: IVisibilityRule;

  // Parse if it's a string
  if (typeof field.visibilityRules === 'string') {
    try {
      parsedRules = JSON.parse(field.visibilityRules);
    } catch {
      return undefined; // Return undefined on parse error
    }
  } else {
    parsedRules = field.visibilityRules;
  }

  return parsedRules.parent_label_id;
}

/**
 * Helper function to create a simple visibility condition
 * @param labelId - ID of the field to check
 * @param value - Expected value(s)
 * @param operator - Comparison operator
 * @returns IVisibilityCondition object
 */
export function createVisibilityCondition(
  labelId: number,
  value: string | string[] | number,
  operator: IVisibilityCondition['operator'] = 'equals'
): IVisibilityCondition {
  return {
    label_id: labelId,
    operator,
    value,
  };
}
